import React from "react";
import { dollarFormatter } from "utils";
import { Status } from "components/status";
import convertCVD from "utils/convertCVD";
import { Locale } from "date-fns";
import { StatementGroup } from "../types";
import { formatStatusColor, formatStatementDate, getInvoiceUrl } from "../utils";

interface StatementGroupTableProps {
  group: StatementGroup;
  dateLocale?: Locale;
  cvd_type?: string;
  tr: (key: string) => string;
}

export default function StatementGroupTable({
  group,
  dateLocale,
  cvd_type,
  tr,
}: StatementGroupTableProps) {
  return (
    <div className="statement-group-card border border-slate-200 rounded-xl overflow-hidden shadow-sm print:shadow-none">
      {/* Group Section Header */}
      <div className="bg-slate-100/80 px-4 py-3 border-b border-slate-200 flex items-center justify-between flex-wrap gap-2">
        <div>
          <h4 className="text-sm font-bold text-slate-900 m-0">{group.title}</h4>
          {group.subtitle && (
            <p className="text-xs text-slate-500 m-0 mt-0.5">{group.subtitle}</p>
          )}
        </div>
      </div>

      {/* Group Invoices Table */}
      <div className="overflow-x-auto print:overflow-visible">
        <table className="w-full min-w-[1200px] table-fixed text-left text-xs print:min-w-0">
          <colgroup>
            <col className="w-[12%]" />
            <col className="w-[11%]" />
            <col className="w-[11%]" />
            <col className="w-[22%]" />
            <col className="w-[12%] no-print" />
            <col className="w-[11%]" />
            <col className="w-[10%]" />
            <col className="w-[11%]" />
          </colgroup>
          <thead className="bg-slate-50 text-slate-600 font-semibold border-b border-slate-200">
            <tr>
              <th className="py-2.5 px-4 text-left">{tr("Invoice #")}</th>
              <th className="py-2.5 px-4 text-left">{tr("Date")}</th>
              <th className="py-2.5 px-4 text-left">{tr("Due Date")}</th>
              <th className="py-2.5 px-4 text-left">{tr("Location")}</th>
              <th className="py-2.5 px-4 text-center no-print">{tr("Status")}</th>
              <th className="py-2.5 px-4 text-right">{tr("Total")}</th>
              <th className="py-2.5 px-4 text-right">{tr("Paid")}</th>
              <th className="py-2.5 px-4 text-right">{tr("Amount Due")}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 text-slate-700">
            {group.invoices.map((inv) => {
              const rawColor = formatStatusColor(inv.status?.color);
              const statusColor =
                cvd_type && cvd_type !== "none"
                  ? convertCVD(rawColor, cvd_type)
                  : rawColor;

              return (
                <tr
                  key={inv.id}
                  className="hover:bg-slate-50/80 transition-colors"
                >
                  <td className="py-2.5 px-4 font-semibold text-left whitespace-nowrap">
                    {inv.is_payable ? (
                      <a
                        href={getInvoiceUrl(inv)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:underline hover:text-blue-800 print:text-blue-600 print:underline"
                      >
                        #{inv.id}
                      </a>
                    ) : (
                      <span className="text-slate-700">#{inv.id}</span>
                    )}
                  </td>
                  <td className="py-2.5 px-4 text-left whitespace-nowrap">
                    {formatStatementDate(inv.creation_date, dateLocale)}
                  </td>
                  <td className="py-2.5 px-4 text-left whitespace-nowrap">
                    {formatStatementDate(inv.due_date, dateLocale)}
                  </td>
                  <td
                    className="py-2.5 px-4 text-left"
                    title={inv.property_address || "—"}
                  >
                    <span className="block truncate print:whitespace-normal print:break-words">
                      {inv.property_address || "—"}
                    </span>
                  </td>
                  <td className="py-2.5 px-4 text-center no-print">
                    <Status color={statusColor}>
                      {tr(inv.status?.label || "Unpaid")}
                    </Status>
                  </td>
                  <td className="py-2.5 px-4 text-right whitespace-nowrap tabular-nums font-medium text-slate-900">
                    {dollarFormatter(inv.actual_total)}
                  </td>
                  <td className="py-2.5 px-4 text-right whitespace-nowrap tabular-nums font-medium text-emerald-700">
                    {dollarFormatter(inv.paid_total)}
                  </td>
                  <td className="py-2.5 px-4 text-right whitespace-nowrap tabular-nums font-bold text-amber-900">
                    {dollarFormatter(inv.amount_due)}
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot className="bg-slate-50/80 border-t border-slate-200">
            <tr>
              <td
                colSpan={5}
                className="py-2.5 px-4 text-right text-slate-500 font-medium no-print"
              >
                {group.type === "project" ? tr("Project Subtotal") : tr("Request Subtotal")}:
              </td>
              <td
                colSpan={4}
                className="py-2.5 px-4 text-right text-slate-500 font-medium hidden print:table-cell print-only"
              >
                {group.type === "project" ? tr("Project Subtotal") : tr("Request Subtotal")}:
              </td>
              <td className="py-2.5 px-4 text-right whitespace-nowrap tabular-nums font-semibold text-slate-900">
                {dollarFormatter(group.totalInvoiced)}
              </td>
              <td className="py-2.5 px-4 text-right whitespace-nowrap tabular-nums font-semibold text-emerald-700">
                {dollarFormatter(group.totalPaid)}
              </td>
              <td className="py-2.5 px-4 text-right whitespace-nowrap tabular-nums font-bold text-amber-900">
                {dollarFormatter(group.totalDue)}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}
